import { useState } from "react";
import { Palette, Wand2, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { useBrands } from "../context/BrandContext";
import { api, handleErr } from "../lib/api";

const STYLES = ["Clean & modern", "Bold & vibrant", "Minimal & elegant", "Warm & friendly", "Luxury & premium", "Playful & fun", "Corporate & trustworthy"];
const HEX = /^#[0-9a-fA-F]{6}$/;

const Swatches = ({ colors, testId }) => (
  <div className="flex gap-1.5" data-testid={testId}>{colors.map((c, i) => <span key={i} title={c} className="w-6 h-6 rounded-md border border-white/10" style={{ background: c }} />)}</div>
);

const StyleEditor = ({ open, onClose, brand, onSaved }) => {
  const s = brand.style || {};
  const [f, setF] = useState({ colors: s.colors?.length ? s.colors : ["#FF6B00"], visualStyle: s.visualStyle || STYLES[0], mood: s.mood || "", notes: s.notes || "", enabled: s.enabled ?? true });
  const [busy, setBusy] = useState(false);
  const setColor = (i, v) => setF({ ...f, colors: f.colors.map((c, j) => (j === i ? v : c)) });
  const save = async () => {
    if (!f.colors.every((c) => HEX.test(c))) return toast.error("Colours must be hex codes like #FF6B00");
    setBusy(true);
    try { await api.put(`/brands/${brand.brandId}/style`, f); await onSaved(); toast.success("Brand style saved"); onClose(); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="brand-style-dialog">
        <DialogHeader><DialogTitle>{brand.businessName} · brand style</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="label-mono mb-2">Colours (up to 6)</p>
            <div className="space-y-2">{f.colors.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="color" data-testid={`style-color-picker-${i}`} value={HEX.test(c) ? c : "#000000"} onChange={(e) => setColor(i, e.target.value.toUpperCase())} className="w-9 h-9 rounded bg-transparent border border-slate-700 cursor-pointer" />
                <Input data-testid={`style-color-hex-${i}`} value={c} onChange={(e) => setColor(i, e.target.value)} className="w-32 font-mono" />
                {f.colors.length > 1 && <button data-testid={`style-color-remove-${i}`} onClick={() => setF({ ...f, colors: f.colors.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-red-400"><X size={15} /></button>}
              </div>))}
            </div>
            {f.colors.length < 6 && <button data-testid="style-color-add" onClick={() => setF({ ...f, colors: [...f.colors, "#0B1120"] })} className="mt-2 text-xs text-orange-400 inline-flex items-center gap-1"><Plus size={13} /> Add colour</button>}
          </div>
          <div>
            <p className="label-mono mb-2">Visual style</p>
            <div className="flex flex-wrap gap-2">{STYLES.map((v) => <button key={v} data-testid={`style-option-${v.split(" ")[0].toLowerCase()}`} onClick={() => setF({ ...f, visualStyle: v })} className={`pill ${f.visualStyle === v ? "pill-on" : "pill-off"}`}>{v}</button>)}</div>
          </div>
          <label className="block space-y-1.5"><span className="label-mono">Mood</span><Input data-testid="style-mood-input" value={f.mood} maxLength={120} onChange={(e) => setF({ ...f, mood: e.target.value })} placeholder="e.g. confident, optimistic, premium" /></label>
          <label className="block space-y-1.5"><span className="label-mono">Extra notes</span><Textarea data-testid="style-notes-input" value={f.notes} maxLength={300} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="e.g. natural light photography, no cartoons, rounded shapes" /></label>
          <label className="flex items-center justify-between text-sm text-slate-300"><span>Apply to new images automatically</span><Switch data-testid="style-enabled-switch" checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} /></label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button><Button data-testid="style-save-btn" disabled={busy} onClick={save} className="rounded-full">{busy ? "Saving..." : "Save style"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BrandStyleCard = ({ useStyle, onToggle }) => {
  const { active, reload } = useBrands();
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!active) return null;
  const s = active.style;
  const auto = async () => {
    setBusy(true);
    try { await api.post(`/brands/${active.brandId}/style/auto`); await reload(); toast.success("Colours detected from your brand"); } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <div className="card-n p-4 flex flex-wrap items-center gap-4" data-testid="brand-style-card">
      <div className="w-10 h-10 rounded-lg bg-orange-500/10 text-orange-400 grid place-items-center shrink-0"><Palette size={18} /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Brand style preset</p>
        {s?.colors?.length ? (
          <div className="flex flex-wrap items-center gap-3 mt-1"><Swatches colors={s.colors} testId="brand-style-swatches" /><span className="text-xs text-slate-400" data-testid="brand-style-summary">{s.visualStyle}{s.mood ? ` · ${s.mood}` : ""}{s.enabled === false ? " · paused" : ""}</span></div>
        ) : <p className="text-xs text-slate-500">Detect colours from your logo and website so every image matches your look.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {onToggle && s?.colors?.length > 0 && s.enabled !== false && (
          <label className="flex items-center gap-2 text-xs text-slate-300 mr-2"><Switch data-testid="image-use-style" checked={useStyle} onCheckedChange={onToggle} /> Match brand style</label>
        )}
        <Button data-testid="brand-style-auto-btn" size="sm" variant="outline" disabled={busy} onClick={auto} className="rounded-full"><Wand2 size={13} className="mr-1" />{busy ? "Detecting..." : s ? "Re-detect" : "Auto-detect"}</Button>
        <Button data-testid="brand-style-edit-btn" size="sm" variant="outline" onClick={() => setEdit(true)} className="rounded-full"><Pencil size={13} className="mr-1" />Edit</Button>
      </div>
      {edit && <StyleEditor open={edit} onClose={() => setEdit(false)} brand={active} onSaved={reload} />}
    </div>
  );
};
